import { formatCost, truncateText } from '../../../../helpers/utils';
import { buildFitCrunchLineItemsTheadHtml } from './fitCrunchLineItemsTableFragments';
import { buildMixtLineItemsTheadHtml } from './mixtLineItemsTableFragments';

function escapeHtml(s: string | number | undefined | null): string {
    if (s == null) {
        return '';
    }
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

const tdMixt =
    'padding:4px;font-weight:normal;border-bottom:1px solid #000;';
const tdMixtRight = `${tdMixt}text-align:right;`;

/** HTML for one MIXT line-items row (matches printPackagingSlipMIXT table cells). */
export function renderMixtOrderItemRowHtml(item: any): string {
    const name = item?.product_name ? truncateText(item.product_name, 100) : '-';
    return `
<td style="${tdMixt}">${escapeHtml(item?.master_sku)}</td>
<td style="${tdMixt}">${escapeHtml(name)}</td>
<td style="${tdMixtRight}">${escapeHtml(item?.quantity)}</td>
<td style="${tdMixtRight}">${escapeHtml(formatCost(item?.unit_cost))}</td>
<td style="${tdMixtRight}">${escapeHtml(formatCost('0.00'))}</td>
<td style="${tdMixtRight}">${escapeHtml(formatCost(item?.total_cost))}</td>`;
}

const tdFit =
    "border-bottom:1px solid #000;padding:4px;font-size:12px;";

function fitProductName(item: any): string {
    const itemName = item?.nick_name || item?.product_name || '-';
    return truncateText(itemName, 100);
}

/** HTML for one FitCrunch line-items row (matches printPackaginSlipFitCrunch table cells). */
export function renderFitCrunchOrderItemRowHtml(item: any): string {
    return `
<td style="${tdFit}text-align:left;">${escapeHtml(fitProductName(item))}</td>
<td style="${tdFit}text-align:right;">${escapeHtml(item?.quantity)}</td>
<td style="${tdFit}text-align:right;">${escapeHtml(formatCost(item?.unit_cost))}</td>
<td style="${tdFit}text-align:right;">${escapeHtml(formatCost('0.00'))}</td>
<td style="${tdFit}text-align:right;">${escapeHtml(formatCost(item?.total_cost))}</td>`;
}

function applyHiddenMeasureBox(el: HTMLElement): void {
    el.style.visibility = 'hidden';
    el.style.position = 'absolute';
    el.style.left = '0';
    el.style.top = '0';
    el.style.pointerEvents = 'none';
    el.style.width = '100%';
}

/**
 * Measures MIXT line-item row heights using one table + shared thead (same column widths as the slip).
 * Isolated one-row tables overstated row height (narrow “Product” column → excessive wrapping).
 */
export function measureMixtOrderItemRowHeights(
    container: HTMLElement,
    items: any[],
    renderRow: (item: any) => string,
): number[] {
    const table = document.createElement('table');
    applyHiddenMeasureBox(table);
    Object.assign(table.style, {
        margin: '0',
        padding: '0',
        textAlign: 'left',
        borderCollapse: 'separate',
        borderSpacing: '2px',
        tableLayout: 'fixed',
        fontFamily: '"Source Sans Pro", sans-serif',
        fontSize: '12px',
        color: '#000',
    });
    table.innerHTML = buildMixtLineItemsTheadHtml();
    const tbody = document.createElement('tbody');
    table.appendChild(tbody);
    const rows: HTMLTableRowElement[] = [];
    for (const item of items) {
        const tr = document.createElement('tr');
        tr.innerHTML = renderRow(item);
        tbody.appendChild(tr);
        rows.push(tr);
    }
    container.appendChild(table);
    const heights = rows.map((tr) => tr.getBoundingClientRect().height);
    table.remove();
    return heights;
}

/**
 * Same as MIXT: one coalesced `packing-slip-line-table` + thead so row wrap matches the printed slip.
 */
export function measureFitCrunchOrderItemRowHeights(
    container: HTMLElement,
    items: any[],
    renderRow: (item: any) => string,
): number[] {
    const outer = document.createElement('div');
    applyHiddenMeasureBox(outer);
    const inner = document.createElement('div');
    inner.style.margin = '4px 0 2px';
    inner.style.width = '100%';
    const table = document.createElement('table');
    table.className = 'packing-slip-line-table';
    Object.assign(table.style, {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '2px',
        tableLayout: 'fixed',
        fontFamily: '"Source Sans Pro", sans-serif',
        fontSize: '12px',
        color: '#000',
    });
    table.innerHTML = buildFitCrunchLineItemsTheadHtml();
    const tbody = document.createElement('tbody');
    table.appendChild(tbody);
    const rows: HTMLTableRowElement[] = [];
    for (const item of items) {
        const tr = document.createElement('tr');
        tr.innerHTML = renderRow(item);
        tbody.appendChild(tr);
        rows.push(tr);
    }
    inner.appendChild(table);
    outer.appendChild(inner);
    container.appendChild(outer);
    const heights = rows.map((tr) => tr.getBoundingClientRect().height);
    outer.remove();
    return heights;
}
